import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { favoriteApi, recentViewApi, authApi } from '../services/api';
import { getLocalRecentViews, type LocalRecentView } from '../utils/localRecentViews';
import { getPropertyUrl } from '../utils/formatters';
// Concept A 정책: StatusBadge 미사용 (분양예정 chip만 허용), PriceTag는 design-system 경유
import { PriceTag, StickyHeader, DesktopNav, DesktopPhonePill, BrandLogo, ListCardSkeleton } from '../design-system/components';
import { INQUIRY_PHONE, INQUIRY_PHONE_TEL_LINK } from '../domain/inquiry/PHONE';
import { maskPropertyStatus } from '../domain/property/formatters';

interface PropertyDetail {
  id: number;
  title: string;
  address: string;
  image_url?: string;
  // Phase 7: ProcessedImageField 변형 URL (LCP 최적화)
  image_card_url?: string | null;
  image_thumbnail_url?: string | null;
  min_price?: number;
  max_price?: number;
  status: string;
  house_manage_no?: string | null;
}

interface Favorite {
  id: number;
  property_detail: PropertyDetail;
  created_at: string;
}

interface RecentView {
  id: number;
  property_detail: PropertyDetail;
  updated_at: string;
}

type TabType = 'favorites' | 'recent' | 'settings';

/**
 * ?tab= → 화면 모드 (v2 2026-09-21 배치 6)
 *   - favorites | recent → '관심' 화면 (하단 탭 '관심' = /profile?tab=favorites)
 *   - 그 외(없음·settings) → '마이' 화면 (계정·설정·고객센터/약관 링크)
 */
const tabFromSearch = (search: string): TabType => {
  const t = new URLSearchParams(search).get('tab');
  return t === 'favorites' || t === 'recent' ? t : 'settings';
};

// 마이 — 고객센터·약관 진입점 (모바일에서 푸터가 없어 여기서 제공). 모두 내부 링크 / tel.
const SUPPORT_LINKS: { to: string; label: string }[] = [
  { to: '/business-inquiry', label: '광고·제휴 문의' },
  { to: '/terms', label: '이용약관' },
  { to: '/privacy', label: '개인정보처리방침' },
];

const ChevronRight = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const SupportLinks: React.FC = () => (
  <section className="overflow-hidden rounded-ca-3xl bg-frame shadow-ca-card" aria-label="고객센터">
    <h2 className="px-4 pb-1 pt-4 text-[13px] font-bold text-muted">고객센터</h2>
    <a href={INQUIRY_PHONE_TEL_LINK} className="flex items-center justify-between px-4 py-3 text-[14px] text-ink">
      <span>분양 상담 전화</span>
      <span className="num font-bold text-accent">{INQUIRY_PHONE}</span>
    </a>
    {SUPPORT_LINKS.map((l) => (
      <Link key={l.to} to={l.to} className="flex items-center justify-between border-t border-ca-line px-4 py-3 text-[14px] text-ink">
        <span>{l.label}</span>
        <span className="text-mute2"><ChevronRight /></span>
      </Link>
    ))}
  </section>
);

const ProfilePage: React.FC = () => {
  const { user, kakaoUser, logout, isAuthenticated, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = tabFromSearch(location.search);
  const isInterest = activeTab !== 'settings';
  const setActiveTab = (t: TabType) =>
    navigate(t === 'settings' ? '/profile' : `/profile?tab=${t}`, { replace: true });
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [recentViews, setRecentViews] = useState<RecentView[]>([]);
  const [loading, setLoading] = useState(true);

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ username: '', email: '', phone: '' });
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // 비로그인: /login 강제 이동 대신 로그인 카드 + 고객센터 링크 노출 (모바일 약관 진입점 확보, v2 2026-09-21)
  useEffect(() => {
    if (!isAuthenticated) { setLoading(false); return; }
    loadData();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // 비로그인 '최근 본 단지' — 이 기기(localStorage) 기준 (v2 S4, 2026-09-22 v2h)
  const [localRecent] = useState<LocalRecentView[]>(() => getLocalRecentViews());

  // 알림 설정 — 기존 API 가 있는 '마케팅 정보 수신'만 연결 (GET /auth/kakao/profile/, PATCH /auth/kakao/consent/).
  // 카카오 계정이 아니면(404) null → 토글 숨김. (v2h 2026-09-22)
  const [marketing, setMarketing] = useState<boolean | null>(null);
  const [marketingSaving, setMarketingSaving] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) { setMarketing(null); return; }
    let cancelled = false;
    api.get('/auth/kakao/profile/')
      .then((r) => { if (!cancelled) setMarketing(Boolean(r.data?.kakao_user?.consent_marketing)); })
      .catch(() => { if (!cancelled) setMarketing(null); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);
  const toggleMarketing = async () => {
    if (marketing === null || marketingSaving) return;
    const next = !marketing;
    setMarketingSaving(true);
    try {
      const r = await api.patch('/auth/kakao/consent/', { consent_marketing: next });
      const on = Boolean(r.data?.kakao_user?.consent_marketing ?? next);
      setMarketing(on);
    } catch {
      alert('설정 변경에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setMarketingSaving(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [favData, recentData] = await Promise.all([favoriteApi.getList(), recentViewApi.getList()]);
      setFavorites(favData);
      setRecentViews(recentData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('로그아웃 하시겠습니까?')) { logout(); navigate('/'); }
  };

  const handleStartEdit = () => {
    if (user) {
      setEditForm({ username: user.username || '', email: user.email || '', phone: user.phone || '' });
      setEditError(''); setEditSuccess(''); setIsEditing(true);
    }
  };

  const handleSaveProfile = async () => {
    if (!editForm.username.trim()) { setEditError('이름을 입력해주세요.'); return; }
    try {
      setIsSaving(true); setEditError(''); setEditSuccess('');
      const response = await authApi.updateProfile({
        username: editForm.username.trim(),
        email: editForm.email.trim() || undefined,
        phone: editForm.phone.trim() || undefined
      });
      if (updateUser) updateUser(response.user);
      setEditSuccess('프로필이 수정되었습니다.'); setIsEditing(false);
    } catch (error: any) {
      setEditError(error.response?.data?.error || '프로필 수정에 실패했습니다.');
    } finally { setIsSaving(false); }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== '회원탈퇴') { setEditError('확인 문구를 정확히 입력해주세요.'); return; }
    try {
      setIsDeleting(true);
      await authApi.deleteAccount();
      alert('회원 탈퇴가 완료되었습니다. 이용해 주셔서 감사합니다.');
      logout(); navigate('/');
    } catch (error: any) {
      setEditError(error.response?.data?.error || '회원 탈퇴에 실패했습니다.');
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const desktopHeader = (
    <div className="hidden lg:block bg-frame">
      <div className="mx-auto max-w-[1140px]">
        <StickyHeader
          left={
            <div className="flex items-center gap-6">
              <BrandLogo />
              <DesktopNav />
            </div>
          }
          right={<DesktopPhonePill />}
        />
      </div>
    </div>
  );

  // ── 공용 렌더 조각 ─────────────────────────────
  const thumbOf = (p: PropertyDetail) => p.image_thumbnail_url || p.image_card_url || p.image_url;
  const regionOf = (address?: string) => (address ? address.split(/\s+/).slice(1, 3).join(' ') : '');
  const upcomingLabel = (p: PropertyDetail) => maskPropertyStatus(p.status, (p as any).is_pre_registration === true, (p as any).is_reopen === true);

  const renderPropertyCard = (property: PropertyDetail, dateValue: string) => (
    <button
      onClick={() => navigate(getPropertyUrl(property))}
      className="w-full flex gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
    >
      <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
        {thumbOf(property) ? (
          <img
            src={thumbOf(property)}
            alt={property.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {/* 상태 배지는 '분양예정'만 (대표 결정). 일반 상태(분양중, 마감임박 등) 미노출 */}
        {upcomingLabel(property) && (
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center rounded-full bg-accent-50 border border-accent-line px-2 py-0.5 text-[10.5px] font-bold text-accent-dark">
              {upcomingLabel(property)}
            </span>
          </div>
        )}
        <h3 className="text-sm font-bold text-gray-900 truncate">{property.title}</h3>
        <p className="text-xs text-gray-400 truncate">{property.address}</p>
        <div className="flex items-center justify-between mt-1">
          <PriceTag size="sm" minPrice={property.min_price} maxPrice={property.max_price} />
          <span className="text-xs text-gray-300">{formatDate(dateValue)}</span>
        </div>
      </div>
    </button>
  );

  // PC 관심단지 그리드 카드 (목업 PC 07 .pcard)
  const renderGridCard = (property: PropertyDetail, key: React.Key) => (
    <button
      key={key}
      type="button"
      onClick={() => navigate(getPropertyUrl(property))}
      className="overflow-hidden rounded-ca-3xl bg-frame text-left shadow-ca-card transition-shadow hover:shadow-ca-sheet"
    >
      <div className="relative h-[150px] bg-block">
        {thumbOf(property) && (
          <img src={thumbOf(property)} alt={property.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
        )}
        {upcomingLabel(property) && (
          <span className="absolute left-2.5 top-2.5 rounded-full bg-frame/95 px-2 py-0.5 text-[10.5px] font-bold text-accent-dark">
            {upcomingLabel(property)}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1.5 p-4">
        <b className="truncate text-[15px] font-bold text-ink">{property.title}</b>
        <span className="truncate text-[12.5px] text-muted">{regionOf(property.address)}</span>
        <PriceTag size="sm" minPrice={property.min_price} maxPrice={property.max_price} />
      </div>
    </button>
  );

  const renderEmpty = (message: string) => (
    <div className="py-16 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
      </div>
      <p className="text-gray-400 text-sm mb-4">{message}</p>
      <button onClick={() => navigate('/')} className="px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors">
        매물 둘러보기
      </button>
    </div>
  );

  // 비로그인 '최근 본 단지' — 이 기기 기준 (S4)
  const localRecentAsDetail: { p: PropertyDetail; at: string }[] = localRecent.map((v) => ({
    p: {
      id: v.id,
      title: v.title,
      address: v.address || '',
      image_url: v.image || undefined,
      min_price: v.min_price ?? undefined,
      max_price: v.max_price ?? undefined,
      status: '',
      house_manage_no: v.house_manage_no ?? null,
    },
    at: new Date(v.viewed_at).toISOString(),
  }));

  const callCard = (
    <section className="flex items-center justify-between rounded-ca-3xl bg-ink p-4 text-white">
      <div className="flex flex-col">
        <span className="text-[12px] text-[#CBD5E1]">상담원 연결</span>
        <b className="num text-[18px] font-bold">{INQUIRY_PHONE}</b>
      </div>
      <a href={INQUIRY_PHONE_TEL_LINK} className="rounded-ca-lg bg-accent px-4 py-2 text-[13px] font-bold text-white">
        통화
      </a>
    </section>
  );

  // 비로그인 — S4: 카카오 로그인 유도 + 최근 본 단지(로컬) + 고객센터 + 상담원 연결
  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-paper pb-24">
        {desktopHeader}
        <div className="mx-auto flex max-w-lg flex-col gap-3 px-4 pt-6">
          <h1 className="text-[22px] font-bold tracking-tight text-ink">{isInterest ? '관심 단지' : '마이'}</h1>
          <section className="flex flex-col items-center gap-3 rounded-ca-3xl bg-frame px-5 py-7 text-center shadow-ca-card">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-50">
              <img src="/voonang-mark.png" alt="" className="h-[34px] w-auto" />
            </div>
            <b className="text-[17px] font-bold text-ink">로그인하고 관심단지를 저장하세요</b>
            <p className="text-[13px] leading-5 text-muted">
              혜택 변동 알림을 받을 수 있고
              <br />
              여러 기기에서 관심단지가 동기화됩니다.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="flex h-[50px] w-full items-center justify-center rounded-ca-xl bg-[#FEE500] text-[15px] font-bold text-[#191919]"
            >
              카카오로 3초 만에 시작
            </button>
            <span className="text-[11.5px] text-mute2">로그인 없이도 단지 조회·전화상담은 가능합니다</span>
          </section>

          <section className="overflow-hidden rounded-ca-3xl bg-frame shadow-ca-card" aria-label="최근 본 단지">
            <div className="flex items-center justify-between px-4 py-3.5">
              <span className="text-[14px] text-muted">관심단지</span>
              <span className="rounded-ca-sm bg-block px-2 py-0.5 text-[11px] font-bold text-muted">로그인 필요</span>
            </div>
            <div className="flex items-center justify-between border-t border-ca-line px-4 py-3.5">
              <span className="text-[14px] text-ink">
                최근 본 단지 <b className="num">{localRecent.length}</b>
              </span>
              <span className="text-[11.5px] text-mute2">이 기기 기준</span>
            </div>
            {localRecentAsDetail.length > 0 && (
              <div className="divide-y divide-ca-line border-t border-ca-line">
                {localRecentAsDetail.slice(0, 5).map(({ p, at }) => (
                  <div key={p.id}>{renderPropertyCard(p, at)}</div>
                ))}
              </div>
            )}
          </section>

          {!isInterest && <SupportLinks />}
          {!isInterest && callCard}
        </div>
      </div>
    );
  }

  const tabs: { id: TabType; label: string; count?: number }[] = [
    { id: 'favorites', label: '찜', count: favorites.length },
    { id: 'recent', label: '최근 본', count: recentViews.length },
  ];

  const avatarUrl = (kakaoUser as any)?.thumbnail_image_url || kakaoUser?.profile_image_url || null;
  const avatar = (size: number) =>
    avatarUrl ? (
      <img src={avatarUrl} alt="" className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />
    ) : (
      <div
        className="flex shrink-0 items-center justify-center rounded-full bg-accent-50 font-bold text-accent"
        style={{ width: size, height: size, fontSize: size * 0.38 }}
      >
        {user.username.charAt(0).toUpperCase()}
      </div>
    );

  // PC 좌측 메뉴 (목업 PC 07 .mymenu)
  const menuItem = (label: string, active: boolean, onClick: () => void, count?: number, muted = false) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between rounded-ca-lg px-3.5 py-[11px] text-left text-[14px] font-semibold ${
        active ? 'bg-accent-50 text-accent' : muted ? 'text-muted hover:bg-block' : 'text-body hover:bg-block'
      }`}
    >
      <span>{label}</span>
      {count !== undefined && <b className="num">{loading ? '-' : count}</b>}
    </button>
  );
  const scrollToId = (id: string) => {
    if (activeTab !== 'settings') setActiveTab('settings');
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };
  const sidebar = (
    <aside className="hidden w-[248px] shrink-0 lg:block">
      <div className="flex flex-col gap-3 rounded-ca-3xl bg-frame p-4 shadow-ca-card">
        <div className="flex items-center gap-2.5">
          {avatar(46)}
          <div className="min-w-0">
            <b className="block truncate text-[15px] font-bold text-ink">{user.username} 님</b>
            <span className="block truncate text-[12px] text-mute2">{user.phone || user.email || ''}</span>
          </div>
        </div>
        <nav className="flex flex-col gap-0.5" aria-label="마이 메뉴">
          {menuItem('마이 홈', activeTab === 'settings', () => setActiveTab('settings'))}
          {menuItem('관심단지', activeTab === 'favorites', () => setActiveTab('favorites'), favorites.length)}
          {menuItem('최근 본 단지', activeTab === 'recent', () => setActiveTab('recent'), recentViews.length)}
          {menuItem('알림 설정', false, () => scrollToId('my-notify'))}
          {menuItem('계정 설정', false, () => scrollToId('my-account'))}
          {menuItem('로그아웃', false, handleLogout, undefined, true)}
        </nav>
      </div>
    </aside>
  );

  // 알림 설정 — 기존 API 가 있는 '마케팅 정보 수신'(카카오 계정 동의)만. 신규 분양·혜택 변동 전역 설정 API 는 없어 만들지 않음.
  const notifyCard = marketing !== null && (
    <section id="my-notify" className="scroll-mt-24 rounded-ca-3xl bg-frame px-4 shadow-ca-card lg:px-5" aria-label="알림 설정">
      <h2 className="pb-1 pt-4 text-[13px] font-bold text-muted">알림 설정</h2>
      <div className="flex items-center justify-between gap-3 py-3.5">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[14px] font-semibold text-ink">광고성 정보 수신 동의</span>
          <span className="text-[12px] text-mute2">잔여세대·할인율 변경과 새 분양 소식을 가장 먼저 받아요(카카오톡·문자·전화). 꺼도 혜택 열람은 그대로예요.</span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={marketing}
          aria-label="광고성 정보 수신 동의"
          onClick={toggleMarketing}
          disabled={marketingSaving}
          data-testid="marketing-toggle"
          className={`relative h-[25px] w-11 shrink-0 rounded-full transition-colors disabled:opacity-60 ${marketing ? 'bg-accent' : 'bg-ca-line'}`}
        >
          <i className={`absolute top-[2px] h-[21px] w-[21px] rounded-full bg-white shadow transition-all ${marketing ? 'left-[21px]' : 'left-[2px]'}`} />
        </button>
      </div>
    </section>
  );

  const accountCard = (
    <section id="my-account" aria-label="계정 정보" className="scroll-mt-24 rounded-ca-3xl bg-frame shadow-ca-card">
      <div className="p-4 space-y-4">
      {editSuccess && <div className="p-3 bg-green-50 text-green-700 text-sm rounded-lg">{editSuccess}</div>}
      {editError && !showDeleteConfirm && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{editError}</div>}

      {/* 계정 정보 */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900">계정 정보</h3>
          {!isEditing && (
            <button onClick={handleStartEdit} className="text-xs text-primary-500 font-semibold">수정</button>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-3">
            {['username', 'email', 'phone'].map(field => (
              <div key={field}>
                <label className="block text-xs text-gray-400 mb-1">
                  {field === 'username' ? '이름' : field === 'email' ? '이메일' : '전화번호'}
                </label>
                <input
                  type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                  value={(editForm as any)[field]}
                  onChange={e => setEditForm({ ...editForm, [field]: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                />
              </div>
            ))}
            <div className="flex gap-2">
              <button onClick={() => { setIsEditing(false); setEditError(''); }} disabled={isSaving} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-500">취소</button>
              <button onClick={handleSaveProfile} disabled={isSaving} className="flex-1 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                {isSaving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {[
              { label: '이름', value: user.username },
              { label: '이메일', value: user.email || '미설정' },
              { label: '전화번호', value: user.phone || '미설정' },
            ].map(item => (
              <div key={item.label} className="flex justify-between py-2 border-b border-gray-50 last:border-0">
                <span className="text-sm text-gray-400">{item.label}</span>
                <span className="text-sm text-gray-700">{item.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </section>
  );

  // 회원 탈퇴 — 페이지 맨 아래 작은 글자 링크로 (2026-10-05 대표: 가운데 크게 보이지 않게).
  // 법상 탈퇴(동의 철회)는 가입보다 어렵게 하면 안 되므로 숨기지 않고 위치·크기만 낮춘다.
  const withdrawSection = (
    <div className="px-1 pt-2 text-right" id="my-withdraw">
      {!showDeleteConfirm ? (
        <button
          type="button"
          onClick={() => setShowDeleteConfirm(true)}
          className="text-[12px] text-muted underline underline-offset-2"
        >
          회원 탈퇴
        </button>
      ) : (
        <div className="space-y-3 rounded-ca-3xl bg-frame p-4 text-left shadow-ca-card">
          <p className="text-[13px] font-bold text-ink">회원 탈퇴</p>
          <p className="text-xs text-gray-400">회원 탈퇴 시 모든 데이터가 영구적으로 삭제됩니다.</p>
          {editError && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{editError}</div>}
          <p className="text-xs text-gray-500">탈퇴를 확인하려면 <strong>"회원탈퇴"</strong>를 입력하세요.</p>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={e => setDeleteConfirmText(e.target.value)}
            placeholder="회원탈퇴"
            className="w-full px-3 py-2.5 border border-red-300 rounded-lg text-sm focus:outline-none focus:border-red-500"
          />
          <div className="flex gap-2">
            <button
              onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setEditError(''); }}
              disabled={isDeleting}
              className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-500"
            >취소</button>
            <button
              onClick={handleDeleteAccount}
              disabled={isDeleting || deleteConfirmText !== '회원탈퇴'}
              className="flex-1 py-2.5 bg-red-500 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
            >{isDeleting ? '처리 중...' : '탈퇴 확인'}</button>
          </div>
        </div>
      )}
    </div>
  );

  // 관심·최근 목록 — 모바일 목록 / PC 3열 그리드
  const listFor = (tab: 'favorites' | 'recent', limit?: number) => {
    const rows = tab === 'favorites'
      ? favorites.map((f) => ({ key: f.id, p: f.property_detail, at: f.created_at }))
      : recentViews.map((v) => ({ key: v.id, p: v.property_detail, at: v.updated_at }));
    const shown = limit ? rows.slice(0, limit) : rows;
    if (loading) {
      return (
        <div className="flex flex-col gap-2 rounded-ca-3xl bg-frame p-2 shadow-ca-card">
          {[0, 1, 2].map((i) => <ListCardSkeleton key={i} />)}
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <div className="overflow-hidden rounded-ca-3xl bg-frame shadow-ca-card">
          {renderEmpty(tab === 'favorites' ? '찜한 단지가 없습니다' : '최근 본 단지가 없습니다')}
        </div>
      );
    }
    return (
      <>
        <div className="overflow-hidden rounded-ca-3xl bg-frame shadow-ca-card lg:hidden">
          <div className="divide-y divide-ca-line">{shown.map((r) => <div key={r.key}>{renderPropertyCard(r.p, r.at)}</div>)}</div>
        </div>
        <div className="hidden grid-cols-3 gap-4 lg:grid">{shown.map((r) => renderGridCard(r.p, r.key))}</div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-paper pb-24">
      {desktopHeader}
      <div className="mx-auto flex max-w-lg flex-col gap-3 px-4 pt-6 lg:max-w-[1200px] lg:flex-row lg:items-start lg:gap-6 lg:px-10 lg:pt-7">
        {sidebar}
        <div className="flex min-w-0 flex-1 flex-col gap-3 lg:gap-4">
        {isInterest ? (
          <>
            {/* 관심 — 찜 / 최근 본 (하단 탭 '관심') */}
            <h1 className="text-[22px] font-bold tracking-tight text-ink">{activeTab === 'recent' ? '최근 본 단지' : '관심 단지'}</h1>
            <div className="flex gap-2" role="tablist">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`rounded-full border px-3.5 py-[7px] text-[12px] font-semibold ${
                    activeTab === tab.id ? 'border-ink bg-ink text-white' : 'border-ca-line bg-frame text-body'
                  }`}
                >
                  {tab.label} <span className="num">{tab.count}</span>
                </button>
              ))}
            </div>
            {listFor(activeTab === 'recent' ? 'recent' : 'favorites')}
          </>
        ) : (
          <>
            {/* 마이 — 프로필 · 관심 요약 · 관심단지 · 알림 설정 · 계정 · 고객센터 */}
            <h1 className="sr-only">마이</h1>
            <section className="flex items-center gap-3 rounded-ca-3xl bg-frame p-4 shadow-ca-card lg:hidden">
              {avatar(52)}
              <div className="min-w-0 flex-1">
                <b className="block truncate text-[18px] font-bold text-ink">{user.username} 님</b>
                <span className="block truncate text-[13px] text-muted">{user.phone || user.email || '이메일 미설정'}</span>
              </div>
              {kakaoUser ? (
                <span className="shrink-0 rounded-ca-sm bg-[#FEF08A] px-2 py-1 text-[11px] font-bold text-[#854D0E]">카카오 연결</span>
              ) : (
                <button onClick={handleLogout} className="rounded-ca-lg border border-ca-line px-3 py-1.5 text-[12px] font-bold text-body">
                  로그아웃
                </button>
              )}
            </section>

            <section className="grid grid-cols-2 rounded-ca-3xl bg-frame py-3.5 text-center shadow-ca-card lg:hidden">
              {tabs.map((tab, i) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={i > 0 ? 'border-l border-ca-line' : ''}
                >
                  <b className={`num block text-[18px] font-bold ${i === 0 ? 'text-accent' : 'text-ink'}`}>{loading ? '-' : tab.count}</b>
                  <span className="block text-[12px] text-muted">{i === 0 ? '관심단지' : '최근 본'}</span>
                </button>
              ))}
            </section>

            {/* 관심단지 미리보기 (목업 07 — 모바일 2개 목록 / PC 3열 그리드) */}
            <section className="flex flex-col gap-2" aria-label="관심단지">
              <div className="flex items-center justify-between">
                <h2 className="text-[16px] font-bold text-ink lg:text-[20px]">관심단지</h2>
                <button type="button" onClick={() => setActiveTab('favorites')} className="text-[13px] font-semibold text-muted">
                  전체보기 ›
                </button>
              </div>
              <div className="lg:hidden">{listFor('favorites', 2)}</div>
              <div className="hidden lg:block">{listFor('favorites', 6)}</div>
            </section>

            {notifyCard}
            {accountCard}
            <SupportLinks />
            {kakaoUser && (
              <button onClick={handleLogout} className="rounded-ca-3xl bg-frame px-4 py-3.5 text-left text-[14px] text-muted shadow-ca-card lg:hidden">
                로그아웃
              </button>
            )}
            {withdrawSection}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
